/**
 * Round 110 (2026-02-14) — generic builder-output → mission-asset
 * normalizer.
 *
 * The mission architecture assumes each builder returns a complete
 * asset object. Instead of manually mapping today's known fields
 * (hook, caption, script, ...), this util forwards the builder's
 * output verbatim, minus system metadata (ids / timestamps / user
 * scoping). New fields added to any builder in the future — Runway
 * URLs, ElevenLabs voice IDs, transcripts, thumbnails, SEO
 * metadata, keywords — will automatically flow back into Today's
 * Mission without requiring changes here.
 *
 * The only non-system rewrite is the ``selected_hook → hook``
 * alias so the dashboard's ExecutionPanel can read the field
 * under its canonical name; the original key is preserved so
 * downstream consumers that already expect ``selected_hook`` keep
 * working.
 */

// System metadata that must NEVER be written back onto the
// mission asset row — those belong to the builder's own storage
// (bot_runs, generated_content, etc.). Extend cautiously.
const SYSTEM_KEYS = new Set([
  'id',
  '_id',
  '__v',
  'created_at',
  'updated_at',
  'user_id',
  'business_id',
  'run_id',
  'session_id',
]);

/**
 * @param {object} rawOutput   The builder's success payload (e.g. the
 *                              `data` object at GenerateContentModal.js:591).
 * @param {object} [extras]    Optional extra fields sourced from React
 *                              state (e.g. `platform` selected by the
 *                              customer inside the modal). Only fills
 *                              blank/missing keys — never overwrites.
 * @returns {object}           Normalized meta object ready to be
 *                              persisted onto the mission's
 *                              `asset_registry.meta` via /finalize.
 */
export function normalizeBuilderAsset(rawOutput, extras = {}) {
  if (!rawOutput || typeof rawOutput !== 'object') return {};

  const out = {};
  for (const [k, v] of Object.entries(rawOutput)) {
    if (SYSTEM_KEYS.has(k)) continue;
    if (v === undefined) continue;
    out[k] = v;
  }

  // Canonical rename — ExecutionPanel + all mission renderers
  // expect the field to be called ``hook``.
  if (out.hook === undefined && out.selected_hook !== undefined) {
    out.hook = out.selected_hook;
  }

  // Backfill from caller extras only where the builder itself
  // didn't provide the field.
  const isBlank = (v) => (
    v === undefined
    || v === null
    || (typeof v === 'string' && v.trim() === '')
    || (Array.isArray(v) && v.length === 0)
  );
  for (const [k, v] of Object.entries(extras || {})) {
    if (isBlank(v)) continue;
    if (isBlank(out[k])) out[k] = v;
  }

  return out;
}

export default normalizeBuilderAsset;
